/**
 * @fileoverview Read-only view of one My Page target.
 * @description Opened from a My Page row. Reads the target in full from the backend (GET
 * /sales/targets/{targetId}) and shows its bank and target facts, who it is assigned to (and who it
 * moved from, or is waiting to move to), where payment stands, the bank's contacts and the target's
 * history. The backend decides who may read a target, so a target the viewer can't see shows the
 * same load error as one that doesn't exist. A footer offers one button per action the backend
 * allows the viewer on this target (`allowedActions`); choosing one hands the target back to the
 * page, which opens that action's dialog.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { useMyPageTargetDetails } from '@/features/sales/hooks/useMyPageTargetDetails'
import {
  DISPLAY_STATUS_VARIANT,
  formatMyPageDateTime,
  PRIORITY_VARIANT,
} from '@/features/sales/lib/my-page-status.utils'
import Badge from '@/shared/components/atoms/Badge'
import Skeleton from '@/shared/components/atoms/Skeleton'
import Modal from '@/shared/components/molecules/Modal'

import MyPageActionBar from './MyPageActionBar'

import type {
  MyPagePerson,
  MyPageTargetAction,
  MyPageTargetContact,
  MyPageTargetDetails,
  MyPageTargetEvent,
} from '@/features/sales/types/my-page.types'
import type { TFunction } from 'i18next'
import type { ReactNode } from 'react'

interface MyPageTargetDetailModalProps {
  targetId: string | null
  onClose: () => void
  onAction?: ((action: MyPageTargetAction, target: MyPageTargetDetails) => void) | undefined
}

/**
 * @description One labelled value in a details panel.
 * @param {{ label: string; children: ReactNode; wide?: boolean; testId?: string }} props - The
 * label and value.
 * @param {string} props.label - The field's label.
 * @param {ReactNode} props.children - Its value.
 * @param {boolean} [props.wide] - Span the panel's full width.
 * @param {string} [props.testId] - QA locator for the field; its value gets `<testId>-value`.
 */
function Field({
  label,
  children,
  wide = false,
  testId,
}: {
  label: string
  children: ReactNode
  wide?: boolean
  testId?: string
}) {
  return (
    <div
      className={`flex min-w-0 flex-col gap-0.5 ${wide ? 'col-span-full' : ''}`}
      data-testid={testId}
    >
      <span className="text-[10.5px] font-semibold tracking-[0.7px] text-footer">{label}</span>
      <span
        className="text-[13px] font-medium break-words text-ink"
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {children}
      </span>
    </div>
  )
}

/**
 * @description One titled details panel.
 * @param {{ title: string; children: ReactNode; testId?: string }} props - The panel's title and
 * fields.
 * @param {string} props.title - The panel's title.
 * @param {ReactNode} props.children - Its fields.
 * @param {string} [props.testId] - QA locator for the panel.
 */
function Panel({
  title,
  children,
  testId,
}: {
  title: string
  children: ReactNode
  testId?: string
}) {
  return (
    <div className="flex flex-col gap-2.5" data-testid={testId}>
      <span className="text-[11px] font-bold tracking-[0.8px] text-kicker">{title}</span>
      <div className="grid grid-cols-1 gap-3.5 rounded-lg border border-row-border bg-surface-hover p-4 sm:grid-cols-2">
        {children}
      </div>
    </div>
  )
}

/**
 * @description A person's name with their position, when the backend sent a position the app has a
 * label for.
 * @param {MyPagePerson} person - The person.
 * @param {TFunction} t - The translate function.
 * @returns {string} Their display name, plus the translated position in brackets.
 */
function personLabel(person: MyPagePerson, t: TFunction): string {
  const position = person.positionCode
    ? t(`sales.myWork.detail.positions.${person.positionCode}`, { defaultValue: '' })
    : ''
  return position ? `${person.displayName} (${position})` : person.displayName
}

/**
 * @description A bank contact as name, designation, email and phone.
 * @param {{ contact: MyPageTargetContact | null }} props - The contact, or null when none is held.
 * @param {MyPageTargetContact | null} props.contact - The contact.
 */
function ContactValue({ contact }: { contact: MyPageTargetContact | null }) {
  if (!contact) return <>—</>
  return (
    <>
      {contact.name}
      {contact.designation ? ` · ${contact.designation}` : ''}
      <span className="block text-[12.5px] font-normal text-cell">
        {[contact.email, contact.phone].filter(Boolean).join(' · ') || '—'}
      </span>
    </>
  )
}

/**
 * @description One history entry as a sentence-style line: what happened, by whom, and when.
 * @param {{ event: MyPageTargetEvent }} props - The entry.
 * @param {MyPageTargetEvent} props.event - The history entry.
 */
function EventRow({ event }: { event: MyPageTargetEvent }) {
  const { t } = useTranslation()
  const moved =
    event.fromUser && event.toUser
      ? ` · ${event.fromUser.displayName} → ${event.toUser.displayName}`
      : ''
  const stages = event.toStage ? ` · ${event.fromStage ?? '—'} → ${event.toStage}` : ''

  return (
    <li
      data-testid="my-page-detail-event"
      className="flex flex-col gap-0.5 border-b border-row-border py-2 last:border-b-0"
    >
      <span className="text-[13px] font-medium text-ink">
        {t(`sales.myWork.detail.eventTypes.${event.eventType}`)}
        {moved}
        {stages}
      </span>
      <span className="text-xs text-footer">
        {formatMyPageDateTime(event.createdAt)} · {event.actor.displayName}
        {event.reason ? ` · ${event.reason}` : ''}
      </span>
    </li>
  )
}

/**
 * @description The target's own facts: name, period, dates, stage, priority and location.
 * @param {{ target: MyPageTargetDetails }} props - The target.
 * @param {MyPageTargetDetails} props.target - The target in full.
 */
function TargetPanel({ target }: { target: MyPageTargetDetails }) {
  const { t } = useTranslation()
  const d = 'sales.myWork.detail'

  return (
    <Panel title={t(`${d}.targetTitle`)} testId="my-page-detail-target">
      <Field label={t(`${d}.targetName`)} testId="my-page-detail-target-name">
        {target.name}
      </Field>
      <Field label={t(`${d}.period`)} testId="my-page-detail-period">
        {target.periodLabel}
      </Field>
      <Field label={t(`${d}.dates`)} testId="my-page-detail-dates">
        {target.periodStart} → {target.dueDate}
      </Field>
      <Field label={t(`${d}.stage`)} testId="my-page-detail-stage">
        {target.stage.label}
      </Field>
      <Field label={t(`${d}.priority`)} testId="my-page-detail-priority">
        <Badge variant={PRIORITY_VARIANT[target.priority]}>
          {t(`sales.myWork.priorities.${target.priority}`)}
        </Badge>
      </Field>
      <Field label={t(`${d}.location`)} testId="my-page-detail-location">
        {[target.city, target.state].filter(Boolean).join(', ') || '—'}
      </Field>
    </Panel>
  )
}

/**
 * @description Who the target is assigned to, who it came from, and any open reassignment request.
 * @param {{ target: MyPageTargetDetails }} props - The target.
 * @param {MyPageTargetDetails} props.target - The target in full.
 */
function AssignmentPanel({ target }: { target: MyPageTargetDetails }) {
  const { t } = useTranslation()
  const d = 'sales.myWork.detail'
  const pending = target.pendingReassignment
  const from = target.reassignedFrom
  const fromDate = target.reassignedAt ? ` · ${target.reassignedAt.slice(0, 10)}` : ''

  return (
    <Panel title={t(`${d}.assignmentTitle`)} testId="my-page-detail-assignment">
      <Field label={t(`${d}.assignee`)} testId="my-page-detail-assignee">
        {personLabel(target.assignee, t)}
      </Field>
      <Field label={t(`${d}.reassignedFrom`)} testId="my-page-detail-reassigned-from">
        {from ? `${personLabel(from, t)}${fromDate}` : '—'}
      </Field>
      {pending && (
        <Field label={t(`${d}.pendingReassignment`)} wide testId="my-page-detail-pending">
          {t(`${d}.pendingLine`, {
            proposed: pending.proposedAssignee.displayName,
            requester: pending.requestedBy.displayName,
            date: pending.requestedAt.slice(0, 10),
          })}
        </Field>
      )}
    </Panel>
  )
}

/**
 * @description Where payment stands and when it was confirmed (or, failing that, sent).
 * @param {{ target: MyPageTargetDetails }} props - The target.
 * @param {MyPageTargetDetails} props.target - The target in full.
 */
function PaymentPanel({ target }: { target: MyPageTargetDetails }) {
  const { t } = useTranslation()
  const d = 'sales.myWork.detail'
  const paidAt = target.paymentConfirmedAt ?? target.paymentSentAt

  return (
    <Panel title={t(`${d}.paymentTitle`)} testId="my-page-detail-payment">
      <Field label={t(`${d}.paymentState`)} testId="my-page-detail-payment-state">
        {t(`${d}.paymentStates.${target.paymentState}`)}
      </Field>
      <Field label={t(`${d}.paymentDate`)} testId="my-page-detail-payment-date">
        {paidAt ? formatMyPageDateTime(paidAt) : '—'}
      </Field>
    </Panel>
  )
}

/**
 * @description The bank's primary contact and, when one is held, its secondary contact.
 * @param {{ target: MyPageTargetDetails }} props - The target.
 * @param {MyPageTargetDetails} props.target - The target in full.
 */
function ContactsPanel({ target }: { target: MyPageTargetDetails }) {
  const { t } = useTranslation()
  const d = 'sales.myWork.detail'

  return (
    <Panel title={t(`${d}.contactsTitle`)} testId="my-page-detail-contacts">
      <Field label={t(`${d}.primaryContact`)} wide testId="my-page-detail-primary-contact">
        <ContactValue contact={target.contacts.primary} />
      </Field>
      {target.contacts.secondary && (
        <Field label={t(`${d}.secondaryContact`)} wide testId="my-page-detail-secondary-contact">
          <ContactValue contact={target.contacts.secondary} />
        </Field>
      )}
    </Panel>
  )
}

/**
 * @description The target's history, oldest first, or a note that there is none yet.
 * @param {{ events: MyPageTargetEvent[] }} props - The history.
 * @param {MyPageTargetEvent[]} props.events - The target's history entries.
 */
function HistoryPanel({ events }: { events: MyPageTargetEvent[] }) {
  const { t } = useTranslation()
  const d = 'sales.myWork.detail'

  return (
    <div className="flex flex-col gap-2.5" data-testid="my-page-detail-history">
      <span className="text-[11px] font-bold tracking-[0.8px] text-kicker">
        {t(`${d}.historyTitle`)}
      </span>
      {events.length === 0 ? (
        <p className="text-[13px] text-slate" data-testid="my-page-detail-history-empty">
          {t(`${d}.historyNone`)}
        </p>
      ) : (
        <ul className="rounded-lg border border-row-border bg-surface-hover px-4 py-1">
          {events.map((event) => (
            <EventRow key={event.eventId} event={event} />
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * @description The target's panels, once loaded.
 * @param {{ target: MyPageTargetDetails }} props - The target.
 * @param {MyPageTargetDetails} props.target - The target in full.
 */
function TargetSections({ target }: { target: MyPageTargetDetails }) {
  return (
    <div className="flex flex-col gap-5">
      <TargetPanel target={target} />
      <AssignmentPanel target={target} />
      <PaymentPanel target={target} />
      <ContactsPanel target={target} />
      <HistoryPanel events={target.events} />
    </div>
  )
}

/**
 * @component
 * @param {MyPageTargetDetailModalProps} props - Which target, and the close handler.
 * @param {string | null} props.targetId - The target to show, or null to keep it closed.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(action: MyPageTargetAction, target: MyPageTargetDetails) => void} [props.onAction] -
 * Called with an allowed action the viewer chose and the target as shown; when omitted, no
 * actions are offered.
 */
function MyPageTargetDetailModal({ targetId, onClose, onAction }: MyPageTargetDetailModalProps) {
  const { t } = useTranslation()
  const detailsQuery = useMyPageTargetDetails(targetId)

  if (targetId === null) return null

  const target = detailsQuery.data

  if (detailsQuery.isPending) {
    return (
      <Modal
        open
        onClose={onClose}
        title={t('sales.myWork.detail.title')}
        size="lg"
        data-testid="my-page-detail-modal"
      >
        <div className="flex flex-col gap-3" data-testid="my-page-detail-loading">
          <Skeleton height={20} width="60%" />
          <Skeleton height={60} />
          <Skeleton height={60} />
        </div>
      </Modal>
    )
  }

  if (detailsQuery.isError || !target) {
    return (
      <Modal
        open
        onClose={onClose}
        title={t('sales.myWork.detail.title')}
        size="lg"
        data-testid="my-page-detail-modal"
      >
        <p className="text-sm text-danger" data-testid="my-page-detail-load-error">
          {t('sales.myWork.detail.loadError')}
        </p>
      </Modal>
    )
  }

  return (
    <Modal
      open
      onClose={onClose}
      data-testid="my-page-detail-modal"
      title={target.bankName}
      subtitle={
        <div className="flex flex-wrap items-center gap-2.5">
          <span data-testid="my-page-detail-tenant-code">{target.tenantCode}</span>
          <span className="h-2.5 w-px bg-rule" />
          <Badge
            variant={DISPLAY_STATUS_VARIANT[target.displayStatus]}
            dot
            data-testid="my-page-detail-status-badge"
          >
            {t(`sales.myWork.displayStatuses.${target.displayStatus}`)}
          </Badge>
        </div>
      }
      size="lg"
      footer={
        onAction && target.allowedActions.length > 0 ? (
          <MyPageActionBar
            actions={target.allowedActions}
            onAction={(action) => onAction(action, target)}
          />
        ) : undefined
      }
    >
      <TargetSections target={target} />
    </Modal>
  )
}

export default MyPageTargetDetailModal
